/**
 * Movement Activity Attribute Rules
 *
 * Defines attribute behavior for movement activities (delivery/collection)
 * Supports to/from sites, temperature, pressure, and fill level tracking
 */

import * as gqlTypes from "@hydrologiq/gql/graphql"
import type { AttributeConfig } from "../types"
import {
  baseNoteConfig,
  getDefaultAsset,
  quantityForScheduled,
  fillLevelCalculator,
  baseFillLevelConfig,
  temperatureForStarted,
  pressureForStarted,
  batchInherited,
  pressureForCompleted,
  temperatureForCompleted,
} from "../shared"
import { findAttributeValue, findAttributeFromLinkedActivity } from "../shared/helpers"

/**
 * Asset configuration for Scheduled status
 * Editable during creation, read-only when editing existing activity
 */
const assetConfigForScheduled: AttributeConfig = {
  attribute: "asset",
  label: "Asset",
  visibility: "visible",
  // Only editable when creating a new activity (temp ID), not when editing existing
  editable: (ctx) => ctx.activity.id.startsWith("temp"),
  required: true,
  helpText: "Select an asset for this movement",
  order: 1,
  defaultValue: (ctx) => {
    console.log("[assetConfigForScheduled] ctx", ctx)
    return getDefaultAsset(ctx)
  },
}

/**
 * Asset configuration for non-scheduled statuses (Started/Completed)
 * Inherited chronologically from previous status, read-only
 */
const assetConfigInherited = (fromEventKind: gqlTypes.ActivityEventKind): AttributeConfig => ({
  attribute: "asset",
  label: "Asset",
  visibility: "visible",
  editable: false,
  required: true,
  helpText: "Asset for this movement (use Edit Asset button to change)",
  order: 1,
  defaultValue: (ctx) => {
    const assetValue = findAttributeValue(ctx, "asset", [fromEventKind])
    if (assetValue) return assetValue
    return getDefaultAsset(ctx)
  },
})

/**
 * Site inherited from previous status
 */
const siteInherited = (
  label: string,
  siteKind:
    | gqlTypes.ActivityAttributeKind.Site
    | gqlTypes.ActivityAttributeKind.Source
    | gqlTypes.ActivityAttributeKind.Destination,
  fromEventKind: gqlTypes.ActivityEventKind,
  editable: boolean = false
): AttributeConfig => ({
  attribute: siteKind,
  label,
  visibility: "visible",
  editable,
  required: false,
  helpText: editable ? `${label} (editable if not set previously)` : `${label} (inherited)`,
  order: 2,
  defaultValue: (ctx) => {
    // For scheduled status, use order site as default
    if (
      fromEventKind === gqlTypes.ActivityEventKind.Scheduled &&
      ctx?.order?.sites &&
      ctx?.order?.sites?.length &&
      ctx?.order?.sites?.length > 0
    ) {
      // Try both possible structures: site.id or direct id
      const siteId = ctx.order.sites[0]?.site?.id
      console.log("[siteInherited] Using order site for scheduled:", siteId)
      if (siteId) {
        return siteId
      }
    }

    // Otherwise, inherit from previous event kind
    let siteAttr = ctx.activity.attributes?.find(
      (attr) => attr.attribute === siteKind && attr.event_kind === fromEventKind
    )
    const previousEventKind =
      fromEventKind === gqlTypes.ActivityEventKind.Completed
        ? gqlTypes.ActivityEventKind.Started
        : fromEventKind === gqlTypes.ActivityEventKind.Started
        ? gqlTypes.ActivityEventKind.Scheduled
        : fromEventKind
    if (!siteAttr)
      siteAttr = ctx.activity.attributes?.find(
        (attr) => attr.attribute === siteKind && attr.event_kind === previousEventKind
      )
    const inheritedSiteId = siteAttr?.site?.id || null
    console.log("[siteInherited] Using inherited site:", inheritedSiteId)
    return inheritedSiteId
  },
})

/**
 * Fill level for Started - auto-populate from delivery activity's completed fill_level
 * Falls back to manual entry or P+T calculation if not available
 */
const fillLevelForStarted: AttributeConfig = {
  attribute: gqlTypes.ActivityAttributeKind.FillLevel,
  ...baseFillLevelConfig,
  label: "Tank Level (kg)",
  visibility: "iot",
  editable: true,
  required: false,
  helpText: "Auto-populated from delivery activity, or calculate from P+T",
  order: 5,
  defaultValue: (ctx) => {
    console.log("[fillLevelForStarted] ctx", ctx)
    // Try to get fill_level from linked delivery activity (source_links) completed status
    const deliveryFillLevel = findAttributeFromLinkedActivity(
      ctx,
      "source",
      gqlTypes.ActivityAttributeKind.FillLevel,
      gqlTypes.ActivityEventKind.Completed
    )
    console.log("[fillLevelForStarted] deliveryFillLevel", deliveryFillLevel)
    if (deliveryFillLevel) {
      return deliveryFillLevel
    }

    // Fallback to calculation from P+T if available
    return null
  },
  calculator: fillLevelCalculator,
  calculatorDependencies: [gqlTypes.ActivityAttributeKind.Pressure, gqlTypes.ActivityAttributeKind.Temperature],
}

/**
 * Fill level for Started - auto-populate from delivery activity's completed fill_level
 * Falls back to manual entry or P+T calculation if not available
 */
const fillLevelForCompleted: AttributeConfig = {
  attribute: gqlTypes.ActivityAttributeKind.FillLevel,
  ...baseFillLevelConfig,
  label: "Tank Level (kg)",
  visibility: "iot",
  editable: true,
  required: false,
  helpText: "Auto-populated from delivery activity, or calculate from P+T",
  order: 5,
  defaultValue: (ctx) => {
    console.log("[fillLevelForCompleted] ctx", ctx)
    // Try to get fill_level from linked delivery activity (source_links) completed status
    const collectionFillLevel = findAttributeFromLinkedActivity(
      ctx,
      "target",
      gqlTypes.ActivityAttributeKind.FillLevel,
      gqlTypes.ActivityEventKind.Started
    )
    console.log("[fillLevelForCompleted] collectionFillLevel", collectionFillLevel)
    if (collectionFillLevel) {
      return collectionFillLevel
    }

    // Fallback to calculation from P+T if available
    return null
  },
  calculator: fillLevelCalculator,
  calculatorDependencies: [gqlTypes.ActivityAttributeKind.Pressure, gqlTypes.ActivityAttributeKind.Temperature],
}

/**
 * Quantity for Started - show delivered quantity (disabled but visible)
 */
const quantityForStarted: AttributeConfig = {
  attribute: gqlTypes.ActivityAttributeKind.Quantity,
  label: "Delivered (kg)",
  visibility: "calculated",
  editable: true,
  required: false,
  helpText: "Auto-populated from delivery activity",
  order: 6,

  calculator: async (ctx) => {
    if (ctx.order?.quantity) {
      return ctx.order.quantity.toString()
    }
    return ctx.currentValues?.fill_level || ctx.currentValues?.quantity || "0"
  },
  calculatorDependencies: [gqlTypes.ActivityAttributeKind.FillLevel],
}

/**
 * Quantity for Completed - auto-calculate consumed (started fill_level - completed fill_level)
 * Only allow positive values
 */
const quantityConsumedForCompleted: AttributeConfig = {
  attribute: gqlTypes.ActivityAttributeKind.Quantity,
  label: "Consumed (kg)",
  visibility: "calculated",
  editable: false,
  required: false,
  helpText: "Auto-calculated: Started tank level - Completed tank level (consumed hydrogen)",
  order: 6,
  calculator: async (ctx) => {
    const startedAttrs = ctx.activity.attributes?.filter((a) => a.event_kind === gqlTypes.ActivityEventKind.Started)
    // Get started fill level from saved attributes
    const startedFillLevelAttr = startedAttrs?.find((a) => a.attribute === gqlTypes.ActivityAttributeKind.FillLevel)
    // const startedFillLevel = startedFillLevelAttr?.value ? parseFloat(startedFillLevelAttr.value) : null
    const startedQuantityAttr = startedAttrs?.find((a) => a.attribute === gqlTypes.ActivityAttributeKind.Quantity)
    // const startedQuantityValue = startedQuantity?.value ? parseFloat(startedQuantity.value) : null
    const startedAttr = startedQuantityAttr || startedFillLevelAttr
    const startedLevel = startedAttr?.value ? parseFloat(startedAttr.value) : null
    console.log("[quantityConsumedForCompleted] startedLevel", startedLevel)

    // Get completed fill level from currentValues (reactive) or saved attributes (fallback)
    const completedFillLevel = parseFloat(
      (ctx as any).currentValues?.fill_level ||
        ctx.activity.attributes?.find(
          (a) =>
            a.attribute === gqlTypes.ActivityAttributeKind.FillLevel &&
            a.event_kind === gqlTypes.ActivityEventKind.Completed
        )?.value ||
        "0"
    )
    console.log("[quantityConsumedForCompleted] completedFillLevel", completedFillLevel)

    // Calculate consumed: started - completed
    // Only allow positive values (if negative, it means tank was refilled, show 0)
    if (startedLevel !== null) {
      const consumed = startedLevel - completedFillLevel
      return consumed > 0 ? consumed.toFixed(2) : "0"
    }

    return "0"
  },
  calculatorDependencies: [gqlTypes.ActivityAttributeKind.FillLevel],
}

/**
 * Attribute rules for Movement activities
 * Organized by status: Scheduled → Started → Completed
 */
export const FULFILMENT_RULES: Record<gqlTypes.ActivityEventKind, AttributeConfig[]> = {
  [gqlTypes.ActivityEventKind.Pending]: [],

  /**
   * SCHEDULED STATUS
   * Planning phase - set from/to sites, asset
   */
  [gqlTypes.ActivityEventKind.Scheduled]: [
    assetConfigForScheduled,
    siteInherited("Site", gqlTypes.ActivityAttributeKind.Site, gqlTypes.ActivityEventKind.Scheduled, true),
    { ...quantityForScheduled(gqlTypes.ActivityKind.Fulfilment, "Quantity (kg)"), order: 7 },
    { ...baseNoteConfig, order: 8 },
  ],

  /**
   * STARTED STATUS
   * Fulfillment started - auto-populate fill level from delivery activity
   */
  [gqlTypes.ActivityEventKind.Started]: [
    assetConfigInherited(gqlTypes.ActivityEventKind.Scheduled),
    siteInherited("Site", gqlTypes.ActivityAttributeKind.Site, gqlTypes.ActivityEventKind.Scheduled, true),
    { ...pressureForStarted(), order: 3 }, // Pressure and temperature on same row, IoT-enabled
    { ...temperatureForStarted(), order: 4 }, // IoT-enabled
    { ...fillLevelForStarted, order: 5 }, // Tank level right after P/T
    { ...quantityForStarted, order: 6 },
    { ...baseNoteConfig, order: 7 },
    {
      ...batchInherited([gqlTypes.ActivityEventKind.Scheduled], true, undefined, [
        gqlTypes.ActivityEventKind.Started,
        gqlTypes.ActivityEventKind.Completed,
      ]),
      order: 8,
    },
  ],

  /**
   * COMPLETED STATUS
   * Fulfillment complete - auto-calculate consumed quantity
   * Quantity consumed = started fill_level - completed fill_level (only positive values)
   */
  [gqlTypes.ActivityEventKind.Completed]: [
    assetConfigInherited(gqlTypes.ActivityEventKind.Started),
    siteInherited("Site", gqlTypes.ActivityAttributeKind.Site, gqlTypes.ActivityEventKind.Started, false),
    { ...pressureForCompleted(), order: 3 }, // Pressure and temperature on same row, IoT-enabled
    { ...temperatureForCompleted(), order: 4 }, // IoT-enabled
    { ...fillLevelForCompleted, order: 5 }, // Tank level right after P/T
    { ...quantityConsumedForCompleted, order: 6 },
    { ...baseNoteConfig, order: 7 },
    {
      ...batchInherited([gqlTypes.ActivityEventKind.Started], false, undefined, [gqlTypes.ActivityEventKind.Completed]),
      order: 8,
    },
  ],
}
